/*
 * Collection engine — pure logic for the collection page (PLP).
 *
 * The single importable seam for the collection page's pure logic. Contains NO
 * Vue and NO DOM references: every function is pure and takes its lookup tables
 * as arguments.
 *
 * `filterProducts`, `sortByRelevance` and `getEquivalentSizes` are lifted
 * straight out of the inline sections/collection.liquid script and preserve its
 * behaviour exactly. `paginate` (Load More), `sortProducts` and its strategies
 * (Most Popular / Price), and the Content-Tile helpers (`tileInsertionPositions`,
 * `contentTileByIndex`, `CONTENT_TILE_POSITIONS`) back the downstream
 * sort/pagination/content-tile tickets (spec.md → Engine logic extraction;
 * Testing Decisions).
 *
 * Ships to the browser as a plain asset (no build step) and attaches its API to
 * `window.KnobbyCollectionEngine`; imported directly by tests as a CommonJS
 * module via `module.exports`.
 */
;(function (global) {
  'use strict'

  // --- Size helpers -------------------------------------------------------

  // Given a canonical size, return every canonical size that shares its
  // abbreviation (e.g. 'XXXLarge' and '3XLarge' both abbreviate to '3XL').
  // A size with no abbreviation resolves to just itself.
  function getEquivalentSizes(size, sizeAbbrevLookup) {
    if (!size) return []
    var lookup = sizeAbbrevLookup || {}
    var abbr = lookup[size]
    if (!abbr) return [size]
    return Object.entries(lookup)
      .filter(function (entry) {
        return entry[1] === abbr
      })
      .map(function (entry) {
        return entry[0]
      })
  }

  // --- Filtering ----------------------------------------------------------

  // Apply the Body Type / Size / Style / Range / Material facet filters to a
  // product array. Mirrors the facet-filtering portion of the section's
  // `filteredProductsList` computed exactly (search filtering, colour
  // enrichment and sorting are handled by their own steps).
  function filterProducts(products, filterState, options) {
    var opts = options || {}
    var sizeAbbrevLookup = opts.sizeAbbrevLookup || {}
    var state = filterState || {}
    var bodyType = state.body_type || []
    var style = state.style || []
    var range = state.range || []
    var material = state.material || []
    var size = state.size || ''

    var filtered = Array.isArray(products) ? products.slice() : []

    if (bodyType.length) {
      filtered = filtered.filter(function (product) {
        return (
          bodyType.some(function (body) {
            return (
              product.tags &&
              product.tags.find(function (tag) {
                return tag.includes(body)
              })
            )
          }) && product.available
        )
      })
    }

    if (size) {
      var equivalentSizes = getEquivalentSizes(size, sizeAbbrevLookup)
      filtered = filtered.filter(function (product) {
        return product.variants.some(function (variant) {
          if (!variant.available) return false
          var variantOptions = [variant.option1, variant.option2].map(function (option) {
            return option == null ? option : option.trim()
          })
          return equivalentSizes.some(function (candidate) {
            return variantOptions.includes(candidate)
          })
        })
      })
    }

    if (style.length) {
      // Matched on the `style:` namespace rather than the literal "style: X" string,
      // so a tag written without the space still answers to the option it produced.
      const wanted = style.map((value) => String(value).trim().toLowerCase())
      filtered = filtered.filter(
        (product) => productStyleValues(product).some((value) => wanted.includes(value)) && product.available
      )
    }

    if (range.length) {
      filtered = filtered.filter(function (product) {
        return (
          range.some(function (value) {
            return product.tags && product.tags.includes('range: ' + value)
          }) && product.available
        )
      })
    }

    if (material.length) {
      filtered = filtered.filter(function (product) {
        return (
          material.some(function (value) {
            return product.tags && product.tags.includes('material: ' + value)
          }) && product.available
        )
      })
    }

    return filtered
  }

  // --- Relevance sort -----------------------------------------------------

  var PACK5_RE = /\b5[\s-]*pack\b/i
  var PACK3_RE = /\b3[\s-]*pack\b/i

  function relevanceTier(product) {
    var title = product.title || ''
    if (PACK5_RE.test(title)) return 0
    if (PACK3_RE.test(title)) return 1
    return 2
  }

  // Out-of-stock sink rank: in-stock 0, out-of-stock 1. The single source for
  // "out-of-stock sinks to the bottom" shared by the Relevance, Most Popular
  // and Price sorts.
  function stockSinkRank(product) {
    return product && product.available ? 0 : 1
  }

  // Compare two products so out-of-stock always sinks below in-stock; within
  // the same stock group defer to `tieBreak(a, b)`.
  function compareWithStockSink(a, b, tieBreak) {
    var stock = stockSinkRank(a) - stockSinkRank(b)
    if (stock !== 0) return stock
    return tieBreak(a, b)
  }

  function relevancePackSortTs(product) {
    if (typeof product.sort_ts === 'number' && !Number.isNaN(product.sort_ts)) {
      return product.sort_ts
    }
    var d = product.created_at || product.published_at
    return d ? Math.floor(new Date(d).getTime() / 1000) : 0
  }

  // The store's curated Relevance order: 5-packs first, then 3-packs (each most
  // recent first), then everything else with out-of-stock sunk to the bottom.
  // Returns a new array; the input is not mutated.
  function sortByRelevance(products) {
    var list = Array.isArray(products) ? products.slice() : []
    return list.sort(function (a, b) {
      var ta = relevanceTier(a)
      var tb = relevanceTier(b)
      if (ta !== tb) return ta - tb
      if (ta === 0 || ta === 1) {
        return relevancePackSortTs(b) - relevancePackSortTs(a)
      }
      var rs = stockSinkRank(a) - stockSinkRank(b)
      if (rs !== 0) return rs
      return 0
    })
  }

  // --- Pagination ---------------------------------------------------------

  // Cumulative "Load More" slice: page 1 shows the first `pageSize`, page 2 the
  // first `2 * pageSize`, and so on (ADR 0002). Returns the visible slice plus
  // the counts needed for a "Showing X of Y products" label.
  function paginate(products, page, pageSize) {
    var list = Array.isArray(products) ? products : []
    var total = list.length
    var safePage = page > 0 ? Math.floor(page) : 1
    var safeSize = pageSize > 0 ? Math.floor(pageSize) : total
    var shown = Math.min(safePage * safeSize, total)
    return {
      items: list.slice(0, shown),
      total: total,
      shown: shown,
      page: safePage,
      pageSize: safeSize,
      hasMore: shown < total
    }
  }

  // --- Content-Tile insertion positions -----------------------------------

  // From the fixed insertion positions, keep only those that fall within the
  // filtered product count — a tile whose position the grid never reaches is
  // dropped so no dangling tile renders below an empty grid.
  function tileInsertionPositions(productCount, positions) {
    var count = typeof productCount === 'number' && productCount > 0 ? productCount : 0
    return (positions || []).filter(function (position) {
      return position < count
    })
  }

  // The default Content-Tile insertion positions (ADR 0003): woven in after the
  // 4th, 12th, 16th and 22nd products. An insertion index N places a tile before
  // the product at index N — i.e. immediately after the Nth product. Merchants
  // can override these per collection from the theme editor, which is what
  // `parseTilePositions` below reads; this stays the fallback.
  var CONTENT_TILE_POSITIONS = [4, 12, 16, 22]

  // Turn the theme-editor "Banner positions" setting into insertion indices.
  //
  // The setting is free text a merchant types ("4, 10, 16"), so this is
  // deliberately forgiving about *form* — any mix of commas, spaces and
  // newlines separates — and strict about *value*: only whole numbers above
  // zero survive, since index 0 would put a banner before the first product and
  // a fraction can never match a product index.
  //
  // Sorted and de-duplicated because `contentTileByIndex` pairs `tiles[i]` with
  // `positions[i]`: unsorted input would drop the first authored banner lower
  // down the grid than the second, and a repeat would collide in its
  // `{ index: tile }` map.
  //
  // Falls back whenever nothing usable survives, rather than returning empty —
  // a typo in the setting should not silently strip every banner off the page.
  function parseTilePositions(raw, fallback) {
    var backstop = Array.isArray(fallback) ? fallback : CONTENT_TILE_POSITIONS
    var parts
    if (Array.isArray(raw)) {
      parts = raw
    } else if (typeof raw === 'string') {
      parts = raw.split(/[\s,]+/)
    } else if (typeof raw === 'number') {
      parts = [raw]
    } else {
      return backstop
    }

    var seen = {}
    var out = []
    for (var i = 0; i < parts.length; i++) {
      var token = String(parts[i]).trim()
      if (!token) continue
      // `Number` (not parseInt) so "7.5" and "4px" are rejected outright rather
      // than truncated to something that looks deliberate.
      var value = Number(token)
      if (!isFinite(value) || Math.floor(value) !== value || value <= 0) continue
      if (seen[value]) continue
      seen[value] = true
      out.push(value)
    }

    if (!out.length) return backstop
    out.sort(function (a, b) {
      return a - b
    })
    return out
  }

  // Pair each authored Content Tile with the product index it renders before,
  // as a plain map `{ index: tile }` the grid can look up per row. `tiles[i]` is
  // placed at `positions[i]` — the pairing is positional so the four tiles keep
  // their fixed order (Brand differentiation, Fabric comparison, Review summary,
  // Guarantee). A tile whose position the filtered grid never reaches is dropped
  // (the shared "drop past the grid" rule from `tileInsertionPositions`) so no
  // tile dangles below an empty grid; a falsy tile entry is skipped rather than
  // mapped. Pure: positions and tile content are supplied by the theme.
  //
  // `leadTile` is the collection's own banner (`collection.banner`), pinned to
  // index 0 so it takes the first slot of the grid ahead of every product. The
  // merchant positions can never claim index 0 (`parseTilePositions` rejects
  // it), so the two never collide. Like any tile it needs a product to sit
  // before, so an empty grid shows none.
  function contentTileByIndex(tiles, positions, productCount, leadTile) {
    var list = Array.isArray(tiles) ? tiles : []
    var pos = Array.isArray(positions) ? positions : []
    var reachable = tileInsertionPositions(productCount, pos)
    var byIndex = {}
    for (var i = 0; i < pos.length && i < list.length; i++) {
      if (list[i] && reachable.indexOf(pos[i]) !== -1) byIndex[pos[i]] = list[i]
    }
    if (leadTile && tileInsertionPositions(productCount, [0]).length) byIndex[0] = leadTile
    return byIndex
  }

  // --- Most Popular Style priority ---------------------------------------

  // Rank a style handle against the resolved Most Popular priority order (the
  // `product_style_details` handles the theme serialises as
  // STYLE_PRIORITY_ORDER — the `style_popularity` metaobject when present, else
  // the theme's hardcoded default). Returns the 0-based index of the handle in
  // `priorityOrder`. A handle absent from the list — or a missing handle —
  // ranks after every listed style (per the glossary), returned as a rank one
  // past the end so all absent styles tie and keep their input order under a
  // stable sort. Pure: the caller supplies the resolved order; this module
  // holds no default of its own.
  function styleRank(styleHandle, priorityOrder) {
    var order = Array.isArray(priorityOrder) ? priorityOrder : []
    if (styleHandle == null) return order.length
    var idx = order.indexOf(String(styleHandle).trim())
    return idx === -1 ? order.length : idx
  }

  // Canonical Style handle for a product, derived from its `style:` tag the same
  // way the Quick Links snippet does (`tag | remove_first: 'style:' | strip`
  // then Liquid `handleize | replace: '-', '_'`) so the Most Popular sort and
  // the Quick Links row agree on every handle. Prefix-matches the `style`
  // namespace (not a substring) so `lifestyle:` is never mistaken for a Style.
  // Returns '' when the product carries no Style tag.
  // The raw value of a product's `style:` tag -- "Men's Trunk", "g string" --
  // or '' where it carries none. Matched on the `style:` NAMESPACE, never as a
  // substring, so a tag like `lifestyle:x` is not mistaken for a style. Pure:
  // takes the product, holds no Vue/DOM.
  const productStyleTag = (product) => {
    if (!product || !Array.isArray(product.tags)) return ''
    for (let i = 0; i < product.tags.length; i++) {
      const tag = product.tags[i]
      if (typeof tag !== 'string') continue
      const colon = tag.indexOf(':')
      if (colon === -1) continue
      if (tag.slice(0, colon).trim().toLowerCase() !== 'style') continue
      return tag.slice(colon + 1).trim()
    }
    return ''
  }

  // Every `style:` tag value a product carries, lower-cased, for the Style filter
  // to match against. Pure.
  const productStyleValues = (product) => {
    if (!product || !Array.isArray(product.tags)) return []
    return product.tags
      .filter((tag) => typeof tag === 'string' && tag.indexOf(':') !== -1)
      .filter((tag) => tag.slice(0, tag.indexOf(':')).trim().toLowerCase() === 'style')
      .map((tag) => tag.slice(tag.indexOf(':') + 1).trim().toLowerCase())
  }

  // The Style filter's options for a set of products: one `{ value, label }` per
  // distinct `style:` tag among the AVAILABLE products. `value` is the tag's own
  // text, which is what the filter state and a `?filter=style:` URL carry; `label`
  // title-cases it ("g string" -> "G String"), which matches the
  // `product_style_details` titles, so the drawer needs no metaobject lookup.
  // Ordered by the Most Popular style priority, unlisted styles after in the
  // order they were first seen. `icon` is the style's `product_style_details`
  // Style Icon, looked up by handle in `stylePills` (window.knobbyStylePills,
  // serialised by snippets/style-pill-map); '' for a style with no entry. Pure.
  const styleFilterOptions = (products, priorityOrder, stylePills) => {
    const pills = stylePills || {}
    const seen = []
    ;(Array.isArray(products) ? products : []).forEach((product) => {
      if (!product || !product.available) return
      const value = productStyleTag(product)
      if (value && !seen.some((option) => option.value === value)) {
        seen.push({ value, handle: productStyleHandle(product) })
      }
    })
    return seen
      .map((option, index) => ({ ...option, index, rank: styleRank(option.handle, priorityOrder) }))
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map(({ value, handle }) => ({
        value,
        label: value.replace(/(^|\s)(\S)/g, (_, space, letter) => space + letter.toUpperCase()),
        icon: (pills[handle] && pills[handle].icon) || ''
      }))
  }

  const productStyleHandle = (product) => {
    const value = productStyleTag(product)
    if (!value) return ''
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
  }

  // --- Sort control (Most Popular / Price) --------------------------------

  var SORT_KEYS = {
    RELEVANCE: 'relevance',
    MOST_POPULAR: 'most-popular',
    PRICE_ASC: 'price-asc',
    PRICE_DESC: 'price-desc'
  }

  // A product's lowest available variant price (spec: "the price used is a
  // product's lowest available variant price"). When nothing is available —
  // an out-of-stock product, which sinks anyway — fall back to the lowest of
  // all variant prices so the OOS group still orders by price. Prices are the
  // integer cents Shopify serialises on each variant. Returns Infinity when no
  // price is resolvable so such products sort last within their group.
  function lowestVariantPrice(product) {
    var variants = product && Array.isArray(product.variants) ? product.variants : []
    var available = variants.filter(function (v) {
      return v && v.available
    })
    var pool = available.length ? available : variants
    var min = Infinity
    for (var i = 0; i < pool.length; i++) {
      var price = Number(pool[i] && pool[i].price)
      if (!Number.isNaN(price) && price < min) min = price
    }
    return min
  }

  // Most Popular: products ordered by their Style following the resolved
  // priority order, with per-Style ties broken by the Relevance order.
  // Out-of-stock products sink to the bottom under this sort like every other
  // (spec.md → Sort engine). Returns a new array; the input is not mutated.
  function sortByMostPopular(products, priorityOrder) {
    var list = Array.isArray(products) ? products.slice() : []
    // Pre-index the Relevance order so it can break per-Style ties in O(1).
    var relIndex = new Map()
    sortByRelevance(list).forEach(function (p, i) {
      relIndex.set(p, i)
    })
    function tieBreak(a, b) {
      var rank = styleRank(productStyleHandle(a), priorityOrder) - styleRank(productStyleHandle(b), priorityOrder)
      if (rank !== 0) return rank
      return relIndex.get(a) - relIndex.get(b)
    }
    return list.sort(function (a, b) {
      return compareWithStockSink(a, b, tieBreak)
    })
  }

  // Price sort: order by each product's lowest available variant price, either
  // ascending ('asc', Low→High) or descending ('desc', High→Low). Out-of-stock
  // products sink to the bottom, price-ordered within each group. Returns a new
  // array; the input is not mutated.
  function sortByPrice(products, direction) {
    var descending = direction === 'desc'
    var list = Array.isArray(products) ? products.slice() : []
    function tieBreak(a, b) {
      var pa = lowestVariantPrice(a)
      var pb = lowestVariantPrice(b)
      if (pa === pb) return 0
      return descending ? pb - pa : pa - pb
    }
    return list.sort(function (a, b) {
      return compareWithStockSink(a, b, tieBreak)
    })
  }

  // Dispatch to a sort strategy by key (SORT_KEYS). Unknown keys and the
  // default fall back to Relevance. `options.priorityOrder` is the resolved
  // Most Popular Style order the Most Popular sort needs.
  function sortProducts(products, sortKey, options) {
    var opts = options || {}
    switch (sortKey) {
      case SORT_KEYS.MOST_POPULAR:
        return sortByMostPopular(products, opts.priorityOrder)
      case SORT_KEYS.PRICE_ASC:
        return sortByPrice(products, 'asc')
      case SORT_KEYS.PRICE_DESC:
        return sortByPrice(products, 'desc')
      case SORT_KEYS.RELEVANCE:
      default:
        return sortByRelevance(products)
    }
  }

  // --- Card pills ---------------------------------------------------------

  // Decide a product card's promo pill from its price data. Pure: takes
  // resolved cent amounts and the `usp` string, holds no Vue/DOM. Returns a
  // pill descriptor `{ label, html }` or `null` when there is no promo pill.
  //
  // A valid compare-at price above the current price yields the plain "Sale"
  // pill (`html: false`, rendered as text) — replacing the old "X% Off"
  // string. Otherwise a non-empty `usp` yields the usp pill (`html: true`,
  // since usp copy may carry markup). Otherwise `null`. The card renders this
  // output verbatim; ticket 05 extends the module with the low-stock pill.
  //
  // `kind` ('sale' | 'usp') names which of the two it is. The pills look alike
  // but rank differently in the card's row: Sale leads it, the merchandising
  // `usp` yields to the product's own New / Low stock signals (see cardPills).
  const promoPill = (input) => {
    const data = input || {}
    const price = Number(data.price)
    const compareAtPrice = Number(data.compareAtPrice)

    const onSale =
      Number.isFinite(compareAtPrice) &&
      compareAtPrice > 0 &&
      Number.isFinite(price) &&
      compareAtPrice > price
    if (onSale) return { label: 'Sale', html: false, kind: 'sale' }

    const usp = data.usp ? String(data.usp).trim() : ''
    if (usp) return { label: usp, html: true, kind: 'usp' }

    return null
  }

  // The store's default "low stock" threshold — the available count at or below
  // which a product is treated as low. Overridable per section (spec.md → the
  // low-stock threshold is a tunable section setting); this is the fallback the
  // pure rule applies when no threshold is supplied.
  const DEFAULT_LOW_STOCK_THRESHOLD = 50

  // Sum purchasable, inventory-tracked stock across a product's variants.
  // Pure: takes the variant array off any storefront product payload, holds no
  // Vue/DOM. The rule is the one `collection-product-json` applies in Liquid,
  // restated here for the surfaces that don't go through it: a variant counts
  // only when Shopify tracks its inventory (`inventory_management` set) AND it
  // stops selling when depleted (`inventory_policy` !== 'continue'), and only
  // positive quantities add in, so an oversold (negative) variant can't drag a
  // sibling's real stock down.
  //
  // The collection grid gets its count from Liquid; every other card surface
  // fetches products from the storefront JSON APIs (/products/{handle}.js and
  // /recommendations/products.json both carry all three fields) and derives it
  // here instead — which is what puts the low-stock badge on those surfaces.
  // A payload with no variants array, or an empty one (predictive search
  // returns `variants: []`), sums to 0 and so shows no badge.
  const availableInventory = (variants) => {
    if (!Array.isArray(variants)) return 0
    let total = 0
    for (let i = 0; i < variants.length; i++) {
      const variant = variants[i]
      if (!variant || !variant.inventory_management) continue
      if (variant.inventory_policy === 'continue') continue
      const quantity = Number(variant.inventory_quantity)
      if (Number.isFinite(quantity) && quantity > 0) total += quantity
    }
    return total
  }

  // Decide a product card's low-stock pill from its available inventory and the
  // store's threshold. Pure: takes the already-summed available count and the
  // threshold, holds no Vue/DOM. Returns a pill descriptor `{ label, modifier }`
  // when the product is genuinely low — available is above zero but at or below
  // the threshold — and `null` otherwise.
  //
  // `available` is the count the theme serialises per product: the sum of stock
  // across purchasable, inventory-tracked variants only (oversellable and
  // untracked variants are excluded upstream, so they contribute nothing and a
  // wholly-untracked product arrives here as 0). A 0 therefore covers both
  // "fully sold out" and "untracked/oversellable" — neither shows the pill, so
  // the signal only ever means "few left, and they can run out". A threshold of
  // 0 (or below) disables the pill entirely.
  const lowStockPill = (input) => {
    const data = input || {}
    const available = Number(data.available)
    let threshold = Number(data.threshold)
    if (!Number.isFinite(threshold)) threshold = DEFAULT_LOW_STOCK_THRESHOLD

    if (!Number.isFinite(available) || available <= 0) return null
    if (threshold <= 0) return null
    if (available > threshold) return null

    return { label: 'Low stock' }
  }

  // Decide a product card's material pill from its tags and the serialised
  // MATERIAL_PILLS lookup. Pure: takes the tag array and the lookup, holds no
  // Vue/DOM. Returns `{ label, icon, background }` for the product's material,
  // or `null` when it carries no `material:` tag — or when that material has no
  // authored presentation.
  //
  // The tag holds the display value ("BottleBlend") while the lookup is keyed by
  // `product_material_details` entry handle ("bottleblend", "recycled-cotton"),
  // so both sides normalise to lowercase with delimiters collapsed to single
  // spaces before they meet.
  //
  // Authored-only is deliberate. The pill is presentation the merchant writes on
  // the metaobject (Pill Title, Pill Icon, Pill Background Colour); a material
  // with no entry would render as a bare label with no glyph — half the design.
  // It also keeps the pill off the card surfaces that never serialise the lookup
  // (search, side cart, home, blog) even though they do pass product tags.
  const materialKey = (value) =>
    String(value == null ? '' : value)
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

  const materialPill = (input) => {
    const data = input || {}
    const tags = Array.isArray(data.tags) ? data.tags : []

    let value = ''
    for (let i = 0; i < tags.length; i++) {
      const tag = String(tags[i] == null ? '' : tags[i]).trim()
      if (!/^material:/i.test(tag)) continue
      value = tag.slice(tag.indexOf(':') + 1).trim()
      if (value) break
    }
    if (!value) return null

    const authored = (data.pills || {})[materialKey(value)]
    if (!authored) return null
    return {
      label: authored.title || value.replace(/\s+/g, ' '),
      icon: authored.icon || '',
      background: authored.background || ''
    }
  }

  // The label the Material filter facet shows for one raw `material:` tag value.
  // Pure: takes the value and the serialised MATERIAL_PILLS lookup.
  //
  // Same authored source as the card badge above — Pill Title on
  // `product_material_details` — so a shopper reads "Performance" in the filter
  // and "Performance" on the tiles it returns, instead of the raw tag value the
  // facet used to print. Only the display changes: the filter state, the
  // checkbox value and the URL all still carry the tag value.
  //
  // Unlike materialPill, an unauthored material still gets a label. A missing
  // badge costs a glyph; a missing facet row costs the shopper the ability to
  // filter on that material at all, so the fallback is the tag value itself.
  const materialLabel = (value, pills) => {
    const raw = String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim()
    if (!raw) return ''
    const authored = (pills || {})[materialKey(raw)]
    return (authored && authored.title) || raw
  }

  // The cut on its own, with any trailing fabric line taken off: "Men's Trunk
  // Naturals" and "Men's Trunk - Naturals" both read "Men's Trunk". Pure: takes
  // the raw style string, holds no Vue/DOM.
  //
  // The catalogue writes the fabric both ways -- with the dash and without -- so
  // the dash is optional here. A card already carries the fabric on its own
  // material pill, and the PDP on a chip beside the style, so leaving it on the
  // style prints the material twice.
  //
  // Only a suffix is stripped, and never the whole string: "Naturals" alone is a
  // style nobody wrote a cut for, not an empty label.
  const MATERIAL_STYLE_SUFFIX = /\s*(?:-\s*)?(?:naturals|merino)\s*$/i

  const styleLabel = (rawStyle) => {
    const value = String(rawStyle == null ? '' : rawStyle).trim()
    if (!value) return ''
    const stripped = value.replace(MATERIAL_STYLE_SUFFIX, '').trim()
    return stripped || value
  }

  // Product types that never carry a cut: the accessories range -- water
  // bottles, towels, bra padding -- whose titles are still written
  // "<thing> / <design>", so the card's title split would otherwise read
  // "750ml Water Bottle" as a style.
  const NON_STYLE_PRODUCT_TYPES = ['accessories']

  // The style line a card prints under its title, or '' for no line at all.
  // Pure: takes the product, holds no Vue/DOM.
  //
  // The line is EARNED by a `style:` tag: a product without one is not a cut,
  // and an accessory is not a cut whatever it is tagged. Only then is the
  // label read -- from the title's own "<style> / <design>" head where the
  // catalogue writes one (so the card prints "Men's Trunk", not the tag's
  // bare "trunk"), and from the tag otherwise.
  const cardStyleLabel = (product) => {
    const data = product || {}
    const tagged = productStyleTag(data)
    if (!tagged) return ''

    const type = String(data.product_type == null ? '' : data.product_type)
      .trim()
      .toLowerCase()
    if (NON_STYLE_PRODUCT_TYPES.indexOf(type) !== -1) return ''

    const title = String(data.title == null ? '' : data.title)
    if (title.indexOf('/') !== -1) {
      const head = title.split('/')[0].trim()
      if (head) return styleLabel(head)
    }
    return styleLabel(tagged)
  }

  // Assemble the card's pill row from the individual pill decisions above.
  // Pure: takes the already-decided descriptors, holds no Vue/DOM. Returns an
  // ordered array of `{ type, label, html }` (material also carries
  // `icon`/`background`), which the card renders in order.
  //
  // TWO pills at most — ONE status pill, then the material pill:
  //
  //     (Sale | New | Low stock | usp) · Material
  //
  // The status pill is chosen by strict precedence, not stacked. A pass that
  // showed every status pill a product earned put Sale, New and TENCEL™ Luxe
  // on one 193px tile, and design QA 2026-09-17 called it back: "there shouldn't
  // be 3 pills, it should show 1 of (new, sale, low stock) if any of those are
  // applicable, as well as the material pill". So a product that is marked down
  // AND newly published AND running out says only the strongest of the three.
  //
  // The authored `usp` ranks last: it is merchandising copy rather than a signal
  // the product earned, so it speaks only when the product earned nothing of its
  // own. Only `kind: 'sale'` is treated as a markdown; any other promo
  // descriptor is a usp.
  //
  // `type` doubles as the BEM modifier the card appends to
  // `.product-card__badge--` — both promo kinds render as `--promo`, as they
  // always have.
  const cardPills = (input) => {
    const data = input || {}
    const pills = []

    const promo = data.promo && data.promo.label ? data.promo : null
    const lowStock = data.lowStock
    const material = data.material
    const promoPillOf = () => ({ type: 'promo', label: promo.label, html: Boolean(promo.html) })

    const isSale = Boolean(promo && promo.kind === 'sale')
    const isNew = Boolean(data.isNew)
    const isLowStock = Boolean(lowStock && lowStock.label)

    if (isSale) pills.push(promoPillOf())
    else if (isNew) pills.push({ type: 'new', label: 'New', html: false })
    else if (isLowStock) pills.push({ type: 'low-stock', label: lowStock.label, html: false })
    // Merchandising copy only when the product earned nothing of its own.
    else if (promo) pills.push(promoPillOf())

    if (material && material.label) {
      pills.push({
        type: 'material',
        label: material.label,
        html: false,
        icon: material.icon || '',
        background: material.background || ''
      })
    }

    return pills
  }

  var api = {
    getEquivalentSizes: getEquivalentSizes,
    promoPill: promoPill,
    lowStockPill: lowStockPill,
    availableInventory: availableInventory,
    materialPill: materialPill,
    materialLabel: materialLabel,
    cardPills: cardPills,
    styleLabel: styleLabel,
    cardStyleLabel: cardStyleLabel,
    productStyleTag: productStyleTag,
    styleFilterOptions: styleFilterOptions,
    DEFAULT_LOW_STOCK_THRESHOLD: DEFAULT_LOW_STOCK_THRESHOLD,
    filterProducts: filterProducts,
    sortByRelevance: sortByRelevance,
    paginate: paginate,
    tileInsertionPositions: tileInsertionPositions,
    contentTileByIndex: contentTileByIndex,
    parseTilePositions: parseTilePositions,
    CONTENT_TILE_POSITIONS: CONTENT_TILE_POSITIONS,
    styleRank: styleRank,
    productStyleHandle: productStyleHandle,
    sortByMostPopular: sortByMostPopular,
    sortByPrice: sortByPrice,
    sortProducts: sortProducts,
    SORT_KEYS: SORT_KEYS
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api
  }
  if (global) {
    global.KnobbyCollectionEngine = api
  }
})(typeof globalThis !== 'undefined' ? globalThis : typeof self !== 'undefined' ? self : this)
